import React from 'react';
import { Container, Stack, Paper, Alert, Button, Checkbox } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useNewsletterForm } from './hooks/useNewsletterForm';
import { HeaderSection } from './forms/HeaderSection';
import { PersonalDataForm } from './forms/PersonalDataForm';
import { ProfessionalInfoForm } from './forms/ProfessionalInfoForm';
import { InterestsForm } from './forms/InterestsForm';
import { SuccessModal } from './forms/SuccessModal';
import { ProcessingModal } from './forms/ProcessingModal';
import { INDUSTRIES } from './constants';
import AltchaWidget from '../Captcha/AltchaWidget';
import LoadingDots from '../Captcha/LoadingDots';
import { API_ENDPOINTS } from '../../config/api';
import './NewsletterRegistration.css';

const NewsletterRegistration: React.FC = () => {
  const {
    form,
    error,
    interestSuggestions,
    isLoadingSuggestions,
    showSuccessModal,
    showProcessingModal,
    modalMode,
    additionalChips,
    handleSubmit,
    handleAddChip,
    handleRemoveChip,
    resetInterestAndPosition,
    setInterestSuggestions,
    setAdditionalChips,
    isPositionConfirmed,
    setIsPositionConfirmed,
    confirmPosition,
    isAltchaVerified,
    altchaError,
    handleAltchaVerified,
    handleAltchaError
  } = useNewsletterForm();

  const handleIndustryChange = (value: string | null) => {
    const oldIndustry = form.values.industry;
    const newIndustry = value || '';
    form.setFieldValue('industry', newIndustry);

    if (oldIndustry !== newIndustry) {
      form.setFieldValue('interests', []);
      setInterestSuggestions([]);
    }
  };

  const handleFinish = () => {
    window.location.href = 'https://www.aclue.de/leistungen#genai-for-business';
  };

  return (
    <Container size="xl">
      {(showSuccessModal || showProcessingModal) && (
        <div className="page-overlay">
          <div className="overlay-animation"></div>
        </div>
      )}

      <Paper
        className="registration-container"
        style={{
          opacity: showSuccessModal || showProcessingModal ? 0.5 : 1,
          pointerEvents: showSuccessModal || showProcessingModal ? 'none' : 'auto'
        }}
      >
        <HeaderSection />

        <AltchaWidget
          challengeUrl={API_ENDPOINTS.altchaChallenge}
          onVerified={handleAltchaVerified}
          onError={handleAltchaError}
          auto={true}
        />

        <form
          onSubmit={event => {
            event.preventDefault();

            const validationResult = form.validate();

            if (!validationResult.hasErrors) {
              handleSubmit(form.values);
            }
          }}
          className="registration-form"
          noValidate
        >
          <Stack gap="xl">
            <PersonalDataForm form={form} />

            <ProfessionalInfoForm
              form={form}
              industries={INDUSTRIES}
              onIndustryChange={handleIndustryChange}
              resetInterestAndPosition={resetInterestAndPosition}
              setInterestSuggestions={setInterestSuggestions}
              setAdditionalChips={setAdditionalChips}
              isPositionConfirmed={isPositionConfirmed}
              setIsPositionConfirmed={setIsPositionConfirmed}
              confirmPosition={confirmPosition}
            />

            <InterestsForm
              form={form}
              isLoadingSuggestions={isLoadingSuggestions}
              interestSuggestions={interestSuggestions}
              additionalChips={additionalChips}
              handleAddChip={handleAddChip}
              handleRemoveChip={handleRemoveChip}
            />

            <Checkbox
              label={
                <>
                  Ich habe die&nbsp;
                  <a href="/datenschutz" target="_blank" rel="noopener noreferrer">
                    Datenschutzerklärung&nbsp;
                  </a>
                  gelesen und stimme zu.
                </>
              }
              {...form.getInputProps('consent', { type: 'checkbox' })}
              data-path="privacyConsent"
            />

            {Object.keys(form.errors).length > 0 && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                Bitte überprüfen Sie alle Pflichtfelder und korrigieren Sie die markierten Fehler.
              </Alert>
            )}

            {altchaError && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Sicherheitsprüfung"
                color="orange"
                variant="light"
              >
                {altchaError}
                {altchaError.includes('länger als erwartet') && (
                  <Button
                    variant="light"
                    size="xs"
                    color="orange"
                    style={{ marginTop: '8px' }}
                    onClick={() => window.location.reload()}
                  >
                    Seite neu laden
                  </Button>
                )}
              </Alert>
            )}

            {error && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Fehler"
                color="red"
                variant="light"
              >
                {error}
              </Alert>
            )}

            <Stack>
              <Button
                type="submit"
                fullWidth
                size="lg"
                disabled={!isAltchaVerified}
                style={{
                  background: !isAltchaVerified
                    ? '#ccc'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none'
                }}
              >
                {isAltchaVerified ? (
                  'Newsletter abonnieren'
                ) : (
                  <LoadingDots text="Sicherheitsprüfung läuft" />
                )}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Paper>

      {/* Processing Modal - zeigt sich sofort beim Klick für besseres UX */}
      <ProcessingModal opened={showProcessingModal} />

      {/* Success Modal - zeigt sich nach API-Response */}
      <SuccessModal opened={showSuccessModal} onFinish={handleFinish} mode={modalMode} />
    </Container>
  );
};

export default NewsletterRegistration;
